import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Crown, Minus, Sparkles, Zap } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useEffect } from "react";
import { API, useRx } from "@/context/AuthContext";

const CYCLES = [["monthly", "Monthly", null], ["quarterly", "Quarterly", "Save 10%"], ["annual", "Annual", "Save 20%"]];
const TIERS = {
  basic: { name: "Basic", icon: Zap, color: "#007AFF", tag: "For active traders", perks: ["All 6 trading windows, every signal", "Suggested strikes, SL, Target & Trailing", "Live charts 1m / 5m / 15m", "Signal history & performance"] },
  pro: { name: "Pro", icon: Crown, color: "#00FF66", tag: "For strategy builders", perks: ["Everything in Basic", "Pro Trader Desk: custom strategies", "Backtest with adjustable parameters", "Save & re-run unlimited strategies"] },
};
const ROWS = [
  ["Market Pulse & index Strength Scores", "yes", "yes", "yes"],
  ["Signals per trading window", "2 preview", "All", "All"],
  ["Option Buyer / Seller, Intraday, Futures, Commodity, Investors", "Preview", "yes", "yes"],
  ["Strike suggestion, SL, Target, Trailing SL status", "yes", "yes", "yes"],
  ["Live charts with 1m / 5m / 15m switch", "yes", "yes", "yes"],
  ["Liquid options, live news, global cues impact", "yes", "yes", "yes"],
  ["Signal history, performance, risk calculator", "yes", "yes", "yes"],
  ["Immersive 3D / AR mode, WhatsApp share cards", "yes", "yes", "yes"],
  ["Pro Trader Desk (build & backtest strategies)", "no", "no", "yes"],
  ["Save & re-run custom strategies", "no", "no", "yes"],
];
const RANK = { free: 0, basic: 1, pro: 2 };
const loadCheckout = () => new Promise((res) => {
  if (window.Razorpay) return res(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => res(true);
  s.onerror = () => res(false);
  document.body.appendChild(s);
});
const inr = (v) => `₹${v.toLocaleString("en-IN")}`;

const Cell = ({ v }) => v === "yes" ? <Check size={16} className="text-[#00FF66] mx-auto" /> : v === "no" ? <Minus size={16} className="text-gray-600 mx-auto" /> : <span className="text-xs text-gray-300">{v}</span>;

export default function PricingView() {
  const { authed, api, me, setMe } = useRx();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("annual");
  const [busy, setBusy] = useState(null);
  const [billing, setBilling] = useState({ mode: "demo", razorpay_enabled: false });
  useEffect(() => {
    axios.get(`${API}/plans`).then((r) => setPlans(r.data.plans)).catch(() => {});
    axios.get(`${API}/billing/config`).then((r) => setBilling(r.data)).catch(() => {});
  }, []);
  const cur = me?.plan || "free";

  const payRazorpay = async (p) => {
    const ok = await loadCheckout();
    if (!ok) throw new Error("Could not load Razorpay checkout");
    const o = await api("post", "/billing/order", { plan_id: p.id });
    await new Promise((resolve) => {
      const rzp = new window.Razorpay({
        key: o.key_id, amount: o.amount, currency: o.currency, order_id: o.order_id,
        name: "Robo-X", description: `${p.name} plan`, theme: { color: "#00FF66" },
        prefill: { email: me?.email || undefined },
        handler: async (res) => {
          try { setMe(await api("post", "/billing/verify", res)); toast.success(`${TIERS[p.tier].name} activated. Payment received.`); }
          catch (e) { toast.error(e.response?.data?.detail || "Payment verification failed"); }
          resolve();
        },
        modal: { ondismiss: () => { toast("Payment cancelled"); resolve(); } },
      });
      rzp.on("payment.failed", (r) => toast.error(r.error?.description || "Payment failed"));
      rzp.open();
    });
  };

  const buy = async (p) => {
    if (!authed) return nav("/sign-up");
    setBusy(p.id);
    try {
      if (billing.razorpay_enabled) await payRazorpay(p);
      else { setMe(await api("post", "/billing/demo-upgrade", { plan_id: p.id })); toast.success(`${TIERS[p.tier].name} activated (demo payment)`); }
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Upgrade failed"); }
    setBusy(null);
  };
  const modeLabel = { demo: "RAZORPAY · DEMO MODE", test: "RAZORPAY · TEST MODE", live: "SECURE PAYMENTS · RAZORPAY" }[billing.mode];

  return (
    <div data-testid="pricing-view">
      <div className="flex flex-col items-center text-center">
        <span className="text-[10px] font-bold tracking-widest text-[#FFCC00] border border-[#FFCC00]/40 rounded-full px-3 py-1" data-testid="payment-mode-badge">{modeLabel}</span>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-5" data-testid="page-title">Choose your <span className="rx-text-glow">edge</span></h1>
        <p className="text-gray-400 mt-3 max-w-xl text-sm">Two plans. Longer cycles save more. Cancel anytime.</p>
        <div className="mt-8 inline-flex p-1 rounded-full bg-black/50 border border-white/10" data-testid="billing-cycle-toggle">
          {CYCLES.map(([k, l, s]) => (
            <button key={k} onClick={() => setCycle(k)} data-testid={`cycle-${k}`}
              className={`relative px-4 sm:px-6 py-2 rounded-full text-sm font-semibold transition-colors ${cycle === k ? "bg-[#171C24] text-white shadow-[0_0_20px_rgba(0,255,102,.15)]" : "text-gray-400 hover:text-white"}`}>
              {l}{s && <span className="ml-2 text-[10px] font-bold text-[#00FF66]">{s}</span>}
            </button>
          ))}
        </div>
      </div>

      {me && cur !== "free" && (
        <div className="max-w-4xl mx-auto mt-8 rx-card p-4 text-sm text-center" data-testid="plan-active">
          You are on <b style={{ color: TIERS[cur].color }}>{TIERS[cur].name}</b> until {new Date(me.plan_until).toLocaleDateString("en-IN")}.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mt-10">
        {["basic", "pro"].map((t) => {
          const p = plans.find((x) => x.tier === t && x.cycle === cycle);
          const T = TIERS[t];
          const lower = RANK[t] < RANK[cur];
          const same = t === cur;
          return (
            <div key={t} className="rx-hero-card p-7 lg:p-8 flex flex-col" style={{ "--tone": T.color, boxShadow: t === "pro" ? "0 0 60px rgba(0,255,102,.12)" : "none" }} data-testid={`plan-card-${t}`}>
              {t === "pro" && <span className="absolute right-5 top-5 inline-flex items-center gap-1 text-[10px] font-bold tracking-widest text-black bg-[#00FF66] rounded-full px-2.5 py-1"><Sparkles size={11} /> MOST POPULAR</span>}
              <div className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-xl grid place-items-center border border-white/10" style={{ background: `${T.color}1A` }}><T.icon size={18} style={{ color: T.color }} /></span>
                <div><div className="font-display text-xl font-bold">{T.name}</div><div className="text-xs text-gray-500">{T.tag}</div></div>
              </div>
              {p ? (
                <div className="mt-7">
                  <div className="flex items-end gap-3">
                    <span className="font-data text-5xl font-bold" data-testid={`plan-price-${t}`}>{inr(p.price_inr)}</span>
                    {p.savings_inr > 0 && <span className="font-data text-base text-gray-500 line-through mb-1.5">{inr(p.list_price_inr)}</span>}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">{p.months === 1 ? "per month" : `for ${p.months} months · ${inr(p.per_month_inr)}/mo`}</div>
                  <div className="h-7 mt-3">
                    {p.savings_inr > 0 && <span className="inline-flex text-xs font-bold text-[#00FF66] bg-[#00FF66]/10 border border-[#00FF66]/30 rounded-full px-3 py-1" data-testid={`plan-savings-${t}`}>You save {inr(p.savings_inr)} ({p.discount_pct}%)</span>}
                  </div>
                </div>
              ) : <div className="h-28 mt-7 animate-pulse rounded-xl bg-white/5" />}
              <ul className="mt-6 space-y-3 text-sm text-gray-300 flex-1">
                {T.perks.map((k) => <li key={k} className="flex gap-2.5"><Check size={16} className="shrink-0 mt-0.5" style={{ color: T.color }} />{k}</li>)}
              </ul>
              <button onClick={() => p && buy(p)} disabled={!p || busy === p?.id || lower} data-testid={`upgrade-btn-${t}`}
                className="mt-8 rounded-full font-bold py-3 transition-[filter,box-shadow] disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110"
                style={{ background: T.color, color: t === "pro" ? "#050505" : "#fff", boxShadow: `0 0 24px ${T.color}40` }}>
                {busy === p?.id ? "Processing…" : !authed ? "Get started" : lower ? "Included in your plan" : same ? (billing.mode === "demo" ? "Extend (demo)" : "Extend plan") : `Upgrade to ${T.name}${billing.mode === "demo" ? " (demo)" : ""}`}
              </button>
            </div>
          );
        })}
      </div>

      <div className="max-w-5xl mx-auto mt-16">
        <h2 className="font-display text-xl font-bold mb-5">Compare plans</h2>
        <div className="rx-card overflow-x-auto rx-scroll">
          <table className="w-full text-sm min-w-[620px]" data-testid="plan-comparison-table">
            <thead><tr className="border-b border-white/10">
              <th className="text-left p-4 rx-label">Feature</th>
              <th className="p-4 rx-label w-28">Free</th>
              <th className="p-4 w-28 text-[#007AFF] text-xs font-bold tracking-widest">BASIC</th>
              <th className="p-4 w-28 text-[#00FF66] text-xs font-bold tracking-widest">PRO</th>
            </tr></thead>
            <tbody>
              {ROWS.map(([f, a, b, c]) => (
                <tr key={f} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                  <td className="p-4 text-gray-300">{f}</td><td className="p-4 text-center"><Cell v={a} /></td>
                  <td className="p-4 text-center"><Cell v={b} /></td><td className="p-4 text-center bg-[#00FF66]/[0.03]"><Cell v={c} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500 mt-5 text-center">{billing.mode === "demo" ? "Payments are in demo mode: no money moves." : billing.mode === "test" ? "Razorpay test mode: use test cards/UPI, no real money moves." : "Payments are processed securely by Razorpay."} {!authed && <Link to="/sign-in" className="text-[#007AFF]">Already a member? Sign in</Link>}</p>
      </div>
    </div>
  );
}
